<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA
    import { SelectBar } from '@oat-sa-private/ui-components';
    import { createEventDispatcher } from 'svelte';
    import { activeTab } from '@/store/navigation';
    import { actionsIcons, actionsList } from '@/core/utils/consts';
    import getAvailableActions from '@/core/utils/getAvailableActions';
    import dynamicKeys from '@/locale/dynamicKeys.js';
    import * as Types from '@/core/model/types.js'; // eslint-disable-line no-unused-vars

    export let rows = [];
    export let interactions = [];

    export let disabled = false;

    const dispatch = createEventDispatcher();

    const checkStates = {
        checked: 'check',
        unchecked: 'empty',
        mixed: 'minus'
    };

    const startActions = []; // disables default 'left arrow' button in SelectBar component

    let actions;

    let hiddenVisibility = true;

    /*
     * SelectBar must be hidden either when requested from consumer
     * or if there is no row selected yet. Once there is a row
     * selected SelectBar does not disappear even if every
     * row is unchecked
     */
    $: hiddenVisibility = disabled ? true : hiddenVisibility;

    /**
     * Allows consumer to reset visibility control
     */
    export function resetVisibility() {
        hiddenVisibility = true;
    }

    $: selectedCount = updateCounter(rows);
    $: if (selectedCount === 0) {
        dispatch('close');
    }

    $: currentState = getCurrentState(selectedCount);

    $: checkedInteractions = getCheckedInteractions(rows, interactions);

    $: if ($activeTab) {
        actions = getAvailableActions($activeTab, checkedInteractions).map(acs => ({
            key: acs,
            label: dynamicKeys()[`acs-${acs}-label-${selectedCount > 1 ? 'plural' : 'singular'}`],
            icon: actionsIcons[acs],
            showLabelText: true,
            ariaHasPopup: 'dialog'
        }));
    }

    /**
     * Updates counter of selected rows. Once counter
     * is > 0 SelectBar is displayed and will not
     * be hidden until user or consumer closes it
     * @param {Array} arr - Array of rows
     * @returns {Number} - Selected rows counter
     */
    function updateCounter(arr) {
        const res = arr.filter(row => row[0].checked).length;

        hiddenVisibility = res === 0;

        return res;
    }

    /**
     * Manages click on SelectBar buttons
     * @param {String} action - clicked action
     */
    function handleClick(action) {
        if (action === 'checkboxChange') {
            updateRowsCheckbox(currentState === checkStates.unchecked || currentState === checkStates.mixed);
        } else if (action === actionsList.authorize) {
            dispatch('bulkAcs', { acs: actionsList.authorize });
        } else if (action === actionsList.report) {
            dispatch('bulkAcs', { acs: actionsList.report });
        } else if (action === actionsList.terminate) {
            dispatch('bulkAcs', { acs: actionsList.terminate });
        } else if (action === actionsList.extendTime) {
            dispatch('bulkAcs', { acs: actionsList.extendTime });
        } else if (action === actionsList.resetExtraTime) {
            dispatch('bulkAcs', { acs: actionsList.resetExtraTime });
        } else if (action === actionsList.reopen) {
            dispatch('bulkAcs', { acs: actionsList.reopen });
        } else if (action === actionsList.pause) {
            dispatch('bulkAcs', { acs: actionsList.pause });
        } else if (action === actionsList.resume) {
            hiddenVisibility = true; // helps to prevent multiple clicks on action button
            dispatch('bulkAcs', { acs: actionsList.resume, useForm: false });
        } else if (action === actionsList.reset) {
            dispatch('bulkAcs', { acs: actionsList.reset });
        } else if (action === 'close') {
            dispatch('close');
            updateRowsCheckbox(false);
            hiddenVisibility = true;
        }
    }

    /**
     * Updates checkbox on rows with the provided status, then dispatches
     * updateRows event so consumer updates their rows copy
     * @param {Boolean} checkStatus - new checkbox status for every row
     */
    function updateRowsCheckbox(checkStatus) {
        rows = rows.map(row => {
            if (!row[0].disabled) {
                row[0].checked = checkStatus;
            }
            return row;
        });

        dispatch('updateRows', { rows });
    }

    /**
     * Get checkedInteractions, depending on which rows are checked and on interactions array
     * @returns {Types.Interaction[]}
     */
    function getCheckedInteractions() {
        const checkedDxIds = rows.filter(row => row[0].checked).map(row => row[0].key);
        return checkedDxIds
            .map(dxId =>
                interactions.find(
                    interaction => interaction.deliveryExecution && interaction.deliveryExecution.id === dxId
                )
            )
            .filter(interaction => interaction);
    }

    /**
     * Get currentState, depending on count of selected rows
     * @returns {String}
     */
    function getCurrentState() {
        if (selectedCount === rows.length) {
            return checkStates.checked;
        } else {
            return selectedCount === 0 ? checkStates.unchecked : checkStates.mixed;
        }
    }
</script>

<style>
    .wrapper {
        min-height: var(--selectbar-height, 4.5rem);
    }

    .hiddenVisibility {
        visibility: hidden;
    }
</style>

<div class="wrapper" class:hiddenVisibility>
    <SelectBar {startActions} {actions} {currentState} {selectedCount} on:action={e => handleClick(e.detail.key)} />
</div>
